import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ArrowLeft, Plus, Minus, X, Wine, UtensilsCrossed, Users, StickyNote, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export default function AddMealPage() {
  const { authHeaders, API } = useAuth();
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState('');
  const [items, setItems] = useState([]);
  const [companions, setCompanions] = useState([]);
  const [existingCompanions, setExistingCompanions] = useState([]);
  const [covers, setCovers] = useState(1);
  const [notes, setNotes] = useState('');
  const [mealDate, setMealDate] = useState(new Date().toISOString().slice(0, 16));
  const [saving, setSaving] = useState(false);

  // Item form
  const [itemName, setItemName] = useState('');
  const [itemType, setItemType] = useState('meal');
  const [itemQty, setItemQty] = useState(1);

  // Companion form
  const [companionName, setCompanionName] = useState('');
  const [showCompanionPicker, setShowCompanionPicker] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [restRes, compRes] = await Promise.all([
          axios.get(`${API}/restaurants`, { headers: authHeaders() }),
          axios.get(`${API}/companions`, { headers: authHeaders() })
        ]);
        setRestaurants(restRes.data);
        setExistingCompanions(compRes.data);
      } catch (err) {
        console.error('Failed to fetch data', err);
      }
    };
    fetchData();
  }, [API, authHeaders]);

  const addItem = () => {
    if (!itemName.trim()) { toast.error('Enter an item name'); return; }
    setItems([...items, { name: itemName.trim(), item_type: itemType, quantity: itemQty }]);
    setItemName('');
    setItemQty(1);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const addCompanion = (name) => {
    const n = name || companionName.trim();
    if (!n) return;
    if (companions.includes(n)) { toast.error('Already added'); return; }
    setCompanions([...companions, n]);
    setCompanionName('');
  };

  const removeCompanion = (name) => {
    setCompanions(companions.filter(c => c !== name));
  };

  const handleSubmit = async () => {
    if (!selectedRestaurant) { toast.error('Please select a restaurant'); return; }
    if (items.length === 0) { toast.error('Add at least one item'); return; }
    
    const restaurant = restaurants.find(r => r.id === selectedRestaurant);
    setSaving(true);
    try {
      await axios.post(`${API}/meals`, {
        restaurant_id: selectedRestaurant,
        restaurant_name: restaurant?.name || 'Unknown',
        items,
        covers,
        companions,
        notes,
        meal_date: new Date(mealDate).toISOString()
      }, { headers: authHeaders() });
      toast.success('Meal logged!');
      navigate('/');
    } catch (err) {
      toast.error('Failed to save meal');
    }
    setSaving(false);
  };

  return (
    <div data-testid="add-meal-page" className="pb-nav px-4 md:px-6 max-w-2xl mx-auto fade-in">
      {/* Header */}
      <div className="flex items-center gap-3 pt-6 pb-4">
        <button
          data-testid="back-button"
          onClick={() => navigate('/')}
          className="w-10 h-10 flex items-center justify-center rounded-xl touch-active"
          style={{ background: '#F0EFEA' }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: '#1A1D21' }} />
        </button>
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#1A1D21' }}>
          Log a Meal
        </h1>
      </div>

      <div className="space-y-5">
        {/* Restaurant Selection */}
        <div className="bg-white rounded-2xl border p-5" style={{ borderColor: '#E8E6E0' }}>
          <Label className="text-base font-medium mb-3 block" style={{ color: '#1A1D21' }}>
            <Store className="w-4 h-4 inline mr-2" style={{ color: '#005A8D' }} />
            Restaurant
          </Label>
          <Select value={selectedRestaurant} onValueChange={setSelectedRestaurant}>
            <SelectTrigger data-testid="restaurant-select" className="h-14 text-lg rounded-xl" style={{ background: '#F9F8F6', borderColor: '#E8E6E0' }}>
              <SelectValue placeholder="Select a restaurant" />
            </SelectTrigger>
            <SelectContent>
              {restaurants.map(r => (
                <SelectItem key={r.id} value={r.id} className="text-base py-3">
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {restaurants.length === 0 && (
            <Button
              data-testid="add-restaurant-cta"
              variant="ghost"
              onClick={() => navigate('/restaurants')}
              className="mt-2 text-sm"
              style={{ color: '#005A8D' }}
            >
              <Plus className="w-4 h-4 mr-1" /> Add a restaurant first
            </Button>
          )}
        </div>

        {/* Date/Time */}
        <div className="bg-white rounded-2xl border p-5" style={{ borderColor: '#E8E6E0' }}>
          <Label className="text-base font-medium mb-3 block" style={{ color: '#1A1D21' }}>
            <Calendar className="w-4 h-4 inline mr-2" style={{ color: '#005A8D' }} />
            Date & Time
          </Label>
          <Input
            data-testid="meal-date-input"
            type="datetime-local"
            className="h-14 text-lg rounded-xl"
            style={{ background: '#F9F8F6', borderColor: '#E8E6E0' }}
            value={mealDate}
            onChange={(e) => setMealDate(e.target.value)}
          />
        </div>

        {/* Covers */}
        <div className="bg-white rounded-2xl border p-5" style={{ borderColor: '#E8E6E0' }}>
          <Label className="text-base font-medium mb-3 block" style={{ color: '#1A1D21' }}>
            <Users className="w-4 h-4 inline mr-2" style={{ color: '#005A8D' }} />
            Covers (people on your tab)
          </Label>
          <div className="flex items-center justify-center gap-6">
            <button
              data-testid="covers-minus"
              onClick={() => setCovers(Math.max(1, covers - 1))}
              className="w-14 h-14 rounded-full flex items-center justify-center touch-active transition-all"
              style={{ background: covers <= 1 ? '#F0EFEA' : '#005A8D', color: covers <= 1 ? '#A0A0A0' : '#FFFFFF' }}
              disabled={covers <= 1}
            >
              <Minus className="w-6 h-6" />
            </button>
            <div className="text-center">
              <span
                data-testid="covers-count"
                className="text-5xl font-semibold block"
                style={{ fontFamily: 'Outfit, sans-serif', color: '#1A1D21' }}
              >
                {covers}
              </span>
              <span className="text-sm" style={{ color: '#5E646E' }}>
                {covers === 1 ? 'person' : 'people'}
              </span>
            </div>
            <button
              data-testid="covers-plus"
              onClick={() => setCovers(covers + 1)}
              className="w-14 h-14 rounded-full flex items-center justify-center touch-active transition-all"
              style={{ background: '#005A8D', color: '#FFFFFF' }}
            >
              <Plus className="w-6 h-6" />
            </button>
          </div>
          <p className="text-xs text-center mt-3" style={{ color: '#5E646E' }}>
            How many people are you paying for? This helps catch double charges.
          </p>
        </div>

        {/* Items */}
        <div className="bg-white rounded-2xl border p-5" style={{ borderColor: '#E8E6E0' }}>
          <Label className="text-base font-medium mb-3 block" style={{ color: '#1A1D21' }}>
            <UtensilsCrossed className="w-4 h-4 inline mr-2" style={{ color: '#005A8D' }} />
            Items Ordered
          </Label>
          
          {/* Existing items */}
          {items.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {items.map((item, i) => (
                <Badge
                  key={i}
                  data-testid={`item-badge-${i}`}
                  className="text-sm rounded-full px-3 py-1.5 font-normal flex items-center gap-1.5"
                  style={{
                    background: item.item_type === 'drink' ? 'rgba(199, 109, 91, 0.12)' : 'rgba(0, 90, 141, 0.08)',
                    color: item.item_type === 'drink' ? '#C76D5B' : '#005A8D',
                    border: 'none'
                  }}
                >
                  {item.item_type === 'drink' ? <Wine className="w-3.5 h-3.5" /> : <UtensilsCrossed className="w-3.5 h-3.5" />}
                  {item.quantity > 1 ? `${item.quantity}x ` : ''}{item.name}
                  <button onClick={() => removeItem(i)} className="ml-1 opacity-60 hover:opacity-100">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {/* Add item form */}
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Input
                data-testid="item-name-input"
                className="h-12 text-base rounded-xl"
                style={{ background: '#F9F8F6', borderColor: '#E8E6E0' }}
                placeholder="e.g. Margarita, Tacos..."
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addItem())}
              />
            </div>
            <Select value={itemType} onValueChange={setItemType}>
              <SelectTrigger data-testid="item-type-select" className="h-12 w-28 rounded-xl" style={{ background: '#F9F8F6', borderColor: '#E8E6E0' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="meal">Meal</SelectItem>
                <SelectItem value="drink">Drink</SelectItem>
              </SelectContent>
            </Select>
            <Input
              data-testid="item-qty-input"
              type="number"
              min="1"
              className="h-12 w-16 text-center rounded-xl"
              style={{ background: '#F9F8F6', borderColor: '#E8E6E0' }}
              value={itemQty}
              onChange={(e) => setItemQty(parseInt(e.target.value) || 1)}
            />
            <Button
              data-testid="add-item-button"
              onClick={addItem}
              className="h-12 w-12 rounded-xl p-0 touch-active"
              style={{ background: '#005A8D' }}
            >
              <Plus className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Companions */}
        <div className="bg-white rounded-2xl border p-5" style={{ borderColor: '#E8E6E0' }}>
          <Label className="text-base font-medium mb-3 block" style={{ color: '#1A1D21' }}>
            <Users className="w-4 h-4 inline mr-2" style={{ color: '#005A8D' }} />
            Who were you with?
          </Label>
          
          {companions.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {companions.map((comp) => (
                <Badge
                  key={comp}
                  data-testid={`companion-badge-${comp}`}
                  className="text-sm rounded-full px-3 py-1.5 font-normal flex items-center gap-1.5"
                  style={{ background: 'rgba(199, 109, 91, 0.12)', color: '#C76D5B', border: 'none' }}
                >
                  {comp}
                  <button onClick={() => removeCompanion(comp)} className="ml-1 opacity-60 hover:opacity-100">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <Input
              data-testid="companion-name-input"
              className="h-12 text-base rounded-xl flex-1"
              style={{ background: '#F9F8F6', borderColor: '#E8E6E0' }}
              placeholder="Add a name..."
              value={companionName}
              onChange={(e) => setCompanionName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCompanion())}
            />
            <Button
              data-testid="add-companion-button"
              onClick={() => addCompanion()}
              className="h-12 w-12 rounded-xl p-0 touch-active"
              style={{ background: '#C76D5B' }}
            >
              <Plus className="w-5 h-5" />
            </Button>
          </div>

          {/* Quick add from existing companions */}
          {existingCompanions.filter(c => !companions.includes(c)).length > 0 && (
            <div className="mt-3">
              <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#5E646E' }}>Quick add</p>
              <div className="flex flex-wrap gap-1.5">
                {existingCompanions.filter(c => !companions.includes(c)).map(c => (
                  <button
                    key={c}
                    data-testid={`quick-add-${c}`}
                    onClick={() => addCompanion(c)}
                    className="text-sm px-3 py-1.5 rounded-full border transition-colors touch-active"
                    style={{ borderColor: '#E8E6E0', color: '#5E646E' }}
                  >
                    + {c}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="bg-white rounded-2xl border p-5" style={{ borderColor: '#E8E6E0' }}>
          <Label className="text-base font-medium mb-3 block" style={{ color: '#1A1D21' }}>
            <StickyNote className="w-4 h-4 inline mr-2" style={{ color: '#005A8D' }} />
            Notes (optional)
          </Label>
          <textarea
            data-testid="notes-input"
            className="w-full h-24 text-base rounded-xl border p-3 resize-none focus:outline-none focus:ring-2"
            style={{ background: '#F9F8F6', borderColor: '#E8E6E0', color: '#1A1D21', fontFamily: 'DM Sans, sans-serif' }}
            placeholder="Any notes about the meal..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        {/* Submit */}
        <Button
          data-testid="save-meal-button"
          onClick={handleSubmit}
          disabled={saving}
          className="w-full h-14 rounded-full text-lg font-medium touch-active mb-4"
          style={{ background: '#005A8D', color: '#FFFFFF' }}
        >
          {saving ? 'Saving...' : 'Save Meal Log'}
        </Button>
      </div>
    </div>
  );
}

function Store(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" />
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" />
      <path d="M2 7h20" />
      <path d="M22 7v3a2 2 0 0 1-2 2a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7" />
    </svg>
  );
}
